import styled, { css } from "styled-components";

// Color tokens for the inspection dashboard — kept inline rather than wiring
// up a full theme since this panel is admin-only and runs in one place.
export const colors = {
  bg: "#0f1115",
  bgRaised: "#171a21",
  bgCard: "#1d212a",
  bgHover: "#252a35",
  border: "#2a3040",
  borderStrong: "#3a4254",
  text: "#e5e7eb",
  textMuted: "#9ca3af",
  textDim: "#6b7280",
  accent: "#6ea8fe",
  accentHover: "#8db8ff",
  ok: "#22c55e",
  warn: "#facc15",
  err: "#ef4444",
  info: "#60a5fa",
  purple: "#a78bfa",
};

export const Shell = styled.div`
  background: ${colors.bg};
  color: ${colors.text};
  border-radius: 14px;
  border: 1px solid ${colors.border};
  width: 100%;
  max-width: 1100px;
  margin-top: 24px;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto,
    sans-serif;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  @media (max-width: 640px) {
    border-radius: 8px;
  }
`;

export const ShellHeader = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  color: ${colors.text};
  text-align: left;
  cursor: pointer;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  border-bottom: 1px solid ${colors.border};
  &:hover {
    background: ${colors.bgRaised};
  }
  @media (max-width: 640px) {
    padding: 12px 14px;
    > span:last-child {
      font-size: 0.72rem;
    }
  }
`;

export const Body = styled.div<{ $open: boolean }>`
  display: ${(p) => (p.$open ? "block" : "none")};
  padding: 18px 20px 22px;
  @media (max-width: 640px) {
    padding: 14px 12px 16px;
  }
`;

export const SectionTitle = styled.h3`
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${colors.textMuted};
  margin: 0 0 10px;
  font-weight: 600;
`;

export const Card = styled.div`
  background: ${colors.bgRaised};
  border: 1px solid ${colors.border};
  border-radius: 10px;
  padding: 14px 16px;
`;

export const Pill = styled.span<{
  $tone?: "ok" | "warn" | "err" | "info" | "muted";
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  border: 1px solid transparent;
  ${(p) => {
    switch (p.$tone) {
      case "ok":
        return css`
          background: rgba(34, 197, 94, 0.12);
          color: ${colors.ok};
          border-color: rgba(34, 197, 94, 0.4);
        `;
      case "warn":
        return css`
          background: rgba(250, 204, 21, 0.12);
          color: ${colors.warn};
          border-color: rgba(250, 204, 21, 0.4);
        `;
      case "err":
        return css`
          background: rgba(239, 68, 68, 0.12);
          color: ${colors.err};
          border-color: rgba(239, 68, 68, 0.4);
        `;
      case "info":
        return css`
          background: rgba(110, 168, 254, 0.12);
          color: ${colors.info};
          border-color: rgba(110, 168, 254, 0.4);
        `;
      default:
        return css`
          background: ${colors.bgCard};
          color: ${colors.textMuted};
          border-color: ${colors.border};
        `;
    }
  }}
`;

export const Btn = styled.button<{ $variant?: "primary" | "ghost" | "danger" }>`
  background: ${(p) =>
    p.$variant === "primary"
      ? colors.accent
      : p.$variant === "danger"
      ? colors.err
      : colors.bgCard};
  color: ${(p) =>
    p.$variant === "primary" || p.$variant === "danger" ? "#0b1020" : colors.text};
  border: 1px solid
    ${(p) =>
      p.$variant === "primary"
        ? colors.accent
        : p.$variant === "danger"
        ? colors.err
        : colors.border};
  font-weight: 600;
  font-size: 0.85rem;
  padding: 7px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: filter 0.15s ease, background 0.15s ease;
  &:hover:not(:disabled) {
    filter: brightness(1.1);
    background: ${(p) =>
      p.$variant === "primary"
        ? colors.accentHover
        : p.$variant === "danger"
        ? "#dc2626"
        : colors.bgHover};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  min-height: 36px;
`;

export const Input = styled.input`
  background: ${colors.bgCard};
  color: ${colors.text};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 0.9rem;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${colors.accent};
    box-shadow: 0 0 0 3px rgba(110, 168, 254, 0.15);
  }
  &[readonly] {
    color: ${colors.textMuted};
    cursor: default;
  }
`;

export const Textarea = styled.textarea`
  background: ${colors.bgCard};
  color: ${colors.text};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.85rem;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  resize: vertical;
  min-height: 80px;
  width: 100%;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${colors.accent};
  }
`;

export const Select = styled.select`
  background: ${colors.bgCard};
  color: ${colors.text};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 0.9rem;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
`;

export const Mono = styled.code`
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.82rem;
  color: ${colors.text};
  background: ${colors.bgCard};
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid ${colors.border};
`;

export const CodeBlock = styled.pre`
  background: #0a0c10;
  color: #cbd5e1;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.78rem;
  line-height: 1.5;
  max-height: 320px;
  overflow: auto;
  margin: 6px 0 0;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const Disclosure = styled.details`
  border: 1px solid ${colors.border};
  border-radius: 10px;
  background: ${colors.bgRaised};
  margin-top: 10px;
  > summary {
    cursor: pointer;
    list-style: none;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    user-select: none;
    font-weight: 600;
    font-size: 0.9rem;
    color: ${colors.text};
  }
  > summary::-webkit-details-marker {
    display: none;
  }
  > summary::before {
    content: "›";
    color: ${colors.textMuted};
    font-size: 1.1rem;
    transition: transform 0.15s ease;
    display: inline-block;
  }
  &[open] > summary::before {
    transform: rotate(90deg);
  }
  > div {
    padding: 0 14px 14px;
  }
`;

export const TableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0 -4px;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.83rem;
  th,
  td {
    padding: 8px 10px;
    text-align: left;
    border-bottom: 1px solid ${colors.border};
  }
  th {
    background: ${colors.bgCard};
    color: ${colors.textMuted};
    font-weight: 600;
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }
  tr:last-child td {
    border-bottom: none;
  }
`;

export const Hint = styled.p`
  color: ${colors.textMuted};
  font-style: italic;
  font-size: 0.85rem;
  margin: 4px 0;
`;

export const ErrorText = styled.p`
  color: ${colors.err};
  font-size: 0.88rem;
  margin: 8px 0;
`;

export const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export const Grid = styled.div<{ $cols?: number; $minColPx?: number }>`
  display: grid;
  gap: 10px;
  grid-template-columns: ${(p) =>
    p.$cols
      ? `repeat(${p.$cols}, 1fr)`
      : `repeat(auto-fit, minmax(${p.$minColPx ?? 220}px, 1fr))`};
`;

export const Label = styled.label`
  display: block;
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${colors.textMuted};
  margin-bottom: 4px;
  font-weight: 600;
`;

export const FieldGroup = styled.div`
  margin-bottom: 12px;
`;

export const KeyValue = styled.div`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  padding: 6px 10px;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.83rem;
  color: ${colors.text};
  word-break: break-word;
  white-space: pre-wrap;
  min-height: 1.2em;
`;
